import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { Subject, Observable, throwError, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { Router } from '@angular/router';

@Injectable({ providedIn: 'root' })
export class AuthorizeService {

  private prefix: string;

  constructor(private http: HttpClient,
    private router: Router,
    @Inject('environment') private environment: any) {
    this.prefix = environment.applicationId;
  }

  get isRememberMe(): boolean {
    return localStorage.getItem(this.prefix + 'isRememberMe') === 'true';
  }
  set isRememberMe(value: boolean) {
    localStorage.setItem(this.prefix + 'isRememberMe', value ? 'true' : 'false');
  }


  get accessToken(): string {
    if (this.isRememberMe) {
      return localStorage.getItem(this.prefix + 'access_token');
    }
    return sessionStorage.getItem(this.prefix + 'access_token');

  }
  set accessToken(value: string) {
    if (this.isRememberMe) {
      localStorage.setItem(this.prefix + 'access_token', value);
      return;
    }
    sessionStorage.setItem(this.prefix + 'access_token', value);
  }

  get refreshToken(): string {
    if (this.isRememberMe) {
      return localStorage.getItem(this.prefix + 'refresh_token');
    }
    return sessionStorage.getItem(this.prefix + 'refresh_token');
  }

  set refreshToken(value: string) {
    if (this.isRememberMe) {
      localStorage.setItem(this.prefix + 'refresh_token', value);
      return;
    }
    sessionStorage.setItem(this.prefix + 'refresh_token', value);
  }

  get userType(): string {
    if (this.isRememberMe) {
      return localStorage.getItem(this.prefix + 'userType');
    }
    return sessionStorage.getItem(this.prefix + 'userType');
  }
  set userType(value: string) {
    if (this.isRememberMe) {
      localStorage.setItem(this.prefix + 'userType', value);
      return;
    }
    sessionStorage.setItem(this.prefix + 'userType', value);
  }

  get userId(): string {
    if (this.isRememberMe) {
      return localStorage.getItem(this.prefix + 'userId');
    }
    return sessionStorage.getItem(this.prefix + 'userId');
  }

  set userId(value: string) {
    if (this.isRememberMe) {
      localStorage.setItem(this.prefix + 'userId', value);
      return;
    }
    sessionStorage.setItem(this.prefix + 'userId', value);
  }

  get userLogin(): string {
    if (this.isRememberMe) {
      return localStorage.getItem(this.prefix + 'userLogin');
    }
    return sessionStorage.getItem(this.prefix + 'userLogin');
  }

  set userLogin(value: string) {
    if (this.isRememberMe) {
      localStorage.setItem(this.prefix + 'userLogin', value);
      return;
    }
    sessionStorage.setItem(this.prefix + 'userLogin', value);
  }

  private httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json'
    })
  };

  public authorize(username: string, password: string): Observable<TokenAuthResponseModel> {

    let model: TokenAuthRequestModel = {
      grant_type: 'password',
      client_id: '',
      client_secret: '',
      username,
      password
    };

    let url = this.environment.authorizeUrl;
    return this.http.post(url, model, this.httpOptions).pipe(

      map((response: TokenAuthResponseModel) => {

        if (response.code !== 200 && response.code < 900) {
          throw new Error(response.message);
        }

        this.processResponse(response);
        return response;
      }));

  }


  public refresh(): Observable<TokenAuthResponseModel> {

    let model: TokenAuthRequestModel = {
      grant_type: 'refresh_token',
      client_id: '',
      client_secret: '',
      refresh_token: this.refreshToken
    };

    let url = this.environment.authorizeUrl;
    return this.http.post(url, model, this.httpOptions).pipe(

      map((response: TokenAuthResponseModel) => {

        if (response.code !== 200) {
          throw new Error(response.message);
        }

        this.processResponse(response);
        return response;

      }),
      catchError((e) => {

        this.clearLocalStorage();

        this.router.navigate(['login']);

        return throwError(e);

      }));

  }


  public processResponse(response: TokenAuthResponseModel): void {

    if (response.code == 200) {

      this.accessToken = response.data.access_token;
      this.refreshToken = response.data.refresh_token;

      let parts = this.accessToken.split(/\./g);
      let payloadJson = atob(parts[1]);
      let payload: any = JSON.parse(payloadJson);

      this.userId = payload.nameid;
      this.userLogin = payload.email;
      this.userType = payload.typ;
    }
    else {
      this.accessToken = null;
      this.refreshToken = null;
      this.userId = null;
      this.userLogin = null;
      this.userType = null;
    }

  }

  public checkAuthorized(): Observable<any> {

    let subject = new Subject<any>();

    if (this.accessToken) {
      return of(true);
    }

    if (!this.refreshToken) {
      return throwError('Invalid refresh token');
    }

    this.refresh().subscribe((r: TokenAuthResponseModel) => {
      subject.next(true);
      subject.complete();
    }, (e: any) => {
      subject.error('Invalid refresh token');
    });

    return subject;
  }

  public clearLocalStorage(): void {

    if(this.isRememberMe) {
      localStorage.removeItem(this.prefix + 'access_token');
      localStorage.removeItem(this.prefix + 'refresh_token');
      localStorage.removeItem(this.prefix + 'userType');
      localStorage.removeItem(this.prefix + 'userId');
      localStorage.removeItem(this.prefix + 'userLogin');
    }
    else
    {
      sessionStorage.removeItem(this.prefix + 'access_token');
      sessionStorage.removeItem(this.prefix + 'refresh_token');
      sessionStorage.removeItem(this.prefix + 'userType');
      sessionStorage.removeItem(this.prefix + 'userId');
      sessionStorage.removeItem(this.prefix + 'userLogin');
    }
 
    // remove last
    localStorage.removeItem(this.prefix + 'isRememberMe');

  }

  public logout(): void {
    this.clearLocalStorage();
  }

}

export class TokenAuthRequestModel {
  public grant_type: string;
  public client_id: string;
  public client_secret: string;
  public username?: string;
  public password?: string;
  public refresh_token?: string;
}

export class TokenAuthResponseModel {
  public code: number;
  public message: string;
  public data: {
    access_token: string;
    refresh_token: string;
    expires_in: number;
  };
}
